/**
 * 使い方モーダルの表示状態を管理するフック
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { acknowledgeUsageGuide, getUsageGuideStatus } from '../services/api';

const STORAGE_KEY_PREFIX = 'usageGuideAcknowledged';

export interface UsageGuideState {
  isOpen: boolean;
  isRequired: boolean;
  openGuide: () => void;
  closeGuide: () => void;
  acknowledge: () => void;
}

// 共用端末で別ユーザーの確認記録を引き継がないよう、キャッシュはユーザー単位で持つ
const storageKey = (userId: string): string => `${STORAGE_KEY_PREFIX}:${userId}`;

const readCache = (userId: string | null): boolean => {
  if (!userId) {
    return false;
  }
  try {
    return localStorage.getItem(storageKey(userId)) === 'true';
  } catch {
    return false;
  }
};

const writeCache = (userId: string | null): void => {
  if (!userId) {
    return;
  }
  try {
    localStorage.setItem(storageKey(userId), 'true');
  } catch {
    // プライベートモード等で保存できなくても表示自体は成立する
  }
};

export const useUsageGuide = (isReady: boolean, userId: string | null): UsageGuideState => {
  const [isOpen, setIsOpen] = useState(false);
  const [isRequired, setIsRequired] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);
  const hasAcknowledgedRef = useRef(false);

  useEffect(() => {
    if (!isReady || hasChecked) {
      return;
    }

    setHasChecked(true);

    const showAsRequired = () => {
      // GET の応答待ちにヘッダーから「わかった」を押した場合は強制表示に戻さない
      if (hasAcknowledgedRef.current) {
        return;
      }
      setIsOpen(true);
      setIsRequired(true);
    };

    getUsageGuideStatus()
      .then(({ acknowledged }) => {
        if (acknowledged) {
          writeCache(userId);
          return;
        }
        showAsRequired();
      })
      .catch(() => {
        // 取得できない場合はキャッシュで判断し、確認記録が無ければ規約を見せる側に倒す
        if (!readCache(userId)) {
          showAsRequired();
        }
      });
  }, [isReady, hasChecked, userId]);

  const openGuide = useCallback(() => {
    setIsRequired(false);
    setIsOpen(true);
  }, []);

  const closeGuide = useCallback(() => {
    if (isRequired) {
      return;
    }
    setIsOpen(false);
  }, [isRequired]);

  const acknowledge = useCallback(() => {
    hasAcknowledgedRef.current = true;
    writeCache(userId);
    setIsOpen(false);
    setIsRequired(false);

    acknowledgeUsageGuide().catch(() => {
      // 記録に失敗してもモーダルは閉じる（次回ロード時にサーバー値で再判定される）
    });
  }, [userId]);

  return { isOpen, isRequired, openGuide, closeGuide, acknowledge };
};
